import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '@/environments/environment';
import {
  SparkAdAccountDto,
  SparkAlertsDto,
  SparkChartMetric,
  SparkCreatorLeaderboardDto,
  SparkCreatorVideosDto,
  SparkForecastDto,
  SparkForecastQuery,
  SparkReportingDto,
  SparkStructureDto,
  SparkTimeseriesDto,
} from '@/app/common/general-types/spark-ads-v3.dto';

/** A `YYYY-MM-DD` window. Omit and the API defaults to the trailing 14 days. */
export interface SparkDateRange {
  startDate: string;
  endDate: string;
}

/**
 * Reads TikTok Spark Ads performance for a brief.
 *
 * Every figure here comes from TikTok `report/integrated/` via the API — none of it
 * is derived client-side.
 */
@Injectable({ providedIn: 'root' })
export class SparkAdsReportingService {
  private readonly http = inject(HttpClient);
  private readonly api = `${environment.shout_api.url}/tiktok-spark-ads`;

  /** KPI row with prior-period deltas. */
  getReporting(briefId: string, range?: SparkDateRange): Observable<SparkReportingDto> {
    return this.http.get<SparkReportingDto>(`${this.api}/reporting/${briefId}`, {
      params: this.rangeParams(range),
    });
  }

  /** Daily series for the performance chart. */
  getTimeseries(
    briefId: string,
    metric: SparkChartMetric,
    range?: SparkDateRange,
  ): Observable<SparkTimeseriesDto> {
    return this.http.get<SparkTimeseriesDto>(`${this.api}/timeseries/${briefId}`, {
      params: { ...this.rangeParams(range), metric },
    });
  }

  /** Campaign › ad group › ad tree with rolled-up metrics. */
  getStructure(briefId: string, range?: SparkDateRange): Observable<SparkStructureDto> {
    return this.http.get<SparkStructureDto>(`${this.api}/structure/${briefId}`, {
      params: this.rangeParams(range),
    });
  }

  /** Creators ranked by spend-weighted ROAS. */
  getCreatorLeaderboard(
    briefId: string,
    range?: SparkDateRange,
  ): Observable<SparkCreatorLeaderboardDto> {
    return this.http.get<SparkCreatorLeaderboardDto>(
      `${this.api}/creator-leaderboard/${briefId}`,
      { params: this.rangeParams(range) },
    );
  }

  /** "Needs You" rows. */
  getAlerts(briefId: string, range?: SparkDateRange): Observable<SparkAlertsDto> {
    return this.http.get<SparkAlertsDto>(`${this.api}/alerts/${briefId}`, {
      params: this.rangeParams(range),
    });
  }

  /**
   * Which TikTok ad account this brief spends through.
   *
   * Falls back to SHOUT's own connection when the brand has not connected theirs —
   * check `usingShoutAccount` and tell the user when it is set.
   */
  getAdAccount(briefId: string): Observable<SparkAdAccountDto> {
    return this.http.get<SparkAdAccountDto>(`${this.api}/ad-account/${briefId}`);
  }

  /**
   * The creator video library: organic metrics, moderation state and Spark auth
   * state in one payload.
   */
  getCreatorVideos(briefId: string): Observable<SparkCreatorVideosDto> {
    return this.http.get<SparkCreatorVideosDto>(`${this.api}/creator-videos/${briefId}`);
  }

  /**
   * Audience estimate for the builder's estimate banners.
   *
   * Check `available` before rendering — it is `false` when TikTok cannot answer, and
   * the UI must say so rather than showing a number.
   */
  getForecast(query: SparkForecastQuery): Observable<SparkForecastDto> {
    const params: Record<string, string> = {
      briefId: query.briefId,
      advertiserId: query.advertiserId,
    };
    if (query.objectiveType) {
      params['objectiveType'] = query.objectiveType;
    }
    if (query.gender) {
      params['gender'] = query.gender;
    }
    this.appendList(params, 'locationIds', query.locationIds);
    this.appendList(params, 'age', query.age);
    this.appendList(params, 'languages', query.languages);
    this.appendList(params, 'interestCategoryIds', query.interestCategoryIds);
    this.appendList(params, 'placements', query.placements);

    return this.http.get<SparkForecastDto>(`${this.api}/forecast`, { params });
  }

  private rangeParams(range?: SparkDateRange): Record<string, string> {
    return range ? { startDate: range.startDate, endDate: range.endDate } : {};
  }

  private appendList(
    params: Record<string, string>,
    key: string,
    values: string[] | undefined,
  ): void {
    if (values && values.length > 0) {
      params[key] = values.join(',');
    }
  }
}
